import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';
import { unparseCurrency } from 'utils';
import { UserGenderLabel, themeColor } from 'constants/index';
import CustomerPropTypes from 'propTypeModels/customer';
import AppBadge from 'components/App/AppBadge';
import Button from 'components/App/Button';
import CustomerAccountTypeBadge from 'components/App/CustomerAccountTypeBadge';
import styles from './CustomerOverview.module.scss';

const CustomerAvatarLarge = ({ src, name }) => {
  const [hasError, setHasError] = React.useState(false);

  if (src && !hasError) {
    return (
      <img
        src={src}
        alt="avatar"
        className="AvatarCircleLarge"
        style={{ objectFit: 'cover' }}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div className="AvatarCircleLarge">
      {name ? name.trim().substring(0, 2).toUpperCase() : 'KH'}
    </div>
  );
};

CustomerAvatarLarge.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
};

CustomerAvatarLarge.defaultProps = {
  src: '',
  name: '',
};

function CustomerOverview({ customerDetails, onClickCancel, onClickBlock }) {
  const { t } = useTranslation();

  // undefined/null = a legacy account, verified before this field existed: show nothing.
  const isEmailUnverified = customerDetails?.emailVerified === false;

  const infoFields = React.useMemo(
    () => [
      {
        label: 'Số điện thoại',
        value: customerDetails?.phoneNum || customerDetails?.phone || '—',
      },
      {
        label: 'Địa chỉ Email',
        value: (
          <span className="d-inline-flex align-items-center">
            {customerDetails?.email || '—'}
            {isEmailUnverified && (
              <AppBadge color="warning" className="ms-2">
                {t('global.email_not_verified')}
              </AppBadge>
            )}
          </span>
        ),
      },
      { label: 'Nơi sinh sống', value: customerDetails?.province || '—' },
      { label: 'Ngày sinh', value: customerDetails?.birthday || '—' },
      {
        label: 'Giới tính',
        value: UserGenderLabel[customerDetails?.gender] || '—',
      },
      { label: 'Quốc gia', value: customerDetails?.country || '—' },
    ],
    [
      customerDetails?.phoneNum,
      customerDetails?.phone,
      customerDetails?.email,
      customerDetails?.province,
      customerDetails?.birthday,
      customerDetails?.gender,
      customerDetails?.country,
      isEmailUnverified,
      t,
    ],
  );

  return (
    <div className={styles.Component}>
      {/* Customer Profile Header Card */}
      <Card className="Section mb-24">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-4">
          <div className="d-flex align-items-center" style={{ gap: '20px' }}>
            <CustomerAvatarLarge
              src={customerDetails?.avatar}
              name={customerDetails?.fullName || customerDetails?.displayName}
            />
            <div className="d-flex flex-column align-items-start justify-content-center">
              <h4 className="customer-name-id">
                {`#${customerDetails?.userId || ''} - ${customerDetails?.fullName || customerDetails?.displayName || ''}`}
              </h4>
              <CustomerAccountTypeBadge
                level={customerDetails?.level}
                className="account-badge"
              />
            </div>
          </div>

          <div className="customer-header-stats">
            <div className="stat-item">
              <p className="stat-label">Ví xu</p>
              <p className="stat-value">{`${customerDetails?.coin || 0} xu`}</p>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <p className="stat-label">Đêm phòng đã đặt</p>
              <p className="stat-value">{customerDetails?.bookedNights || 0}</p>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <p className="stat-label">Lượt đổi quà</p>
              <p className="stat-value">
                {customerDetails?.redeemedGifts ?? 0}
              </p>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <p className="stat-label">Tổng thanh toán</p>
              <p className="stat-value">{`VNĐ ${unparseCurrency(customerDetails?.totalBookingAmount ?? 0)}`}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Detailed Info Card */}
      <Card className="Section mb-24">
        <h5 className="Section__Title mb-20">Thông tin</h5>
        <div className="info-fields-container">
          {infoFields.map((field, idx) => (
            <div
              key={field.label}
              className={`info-field-row ${idx % 2 === 1 ? 'odd-row' : 'even-row'}`}
            >
              <p className="info-field-label">{field.label}</p>
              <p className="info-field-value">{field.value}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Bottom Action Bar */}
      <div className="bottom-action-bar shadow-sm">
        <Button color={themeColor.LIGHT} outline onClick={onClickCancel}>
          Huỷ
        </Button>
        {!customerDetails?.isBlocked && (
          <Button color={themeColor.DANGER} onClick={onClickBlock}>
            Chặn
          </Button>
        )}
      </div>
    </div>
  );
}

CustomerOverview.propTypes = {
  customerDetails: CustomerPropTypes.customer,
  onClickCancel: PropTypes.func,
  onClickBlock: PropTypes.func,
};

CustomerOverview.defaultProps = {
  customerDetails: null,
  onClickCancel: () => {},
  onClickBlock: () => {},
};

export default CustomerOverview;
